import { useState, useEffect, useCallback, useRef } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import { useEvent, useSetRsvp, useRemoveRsvp } from "@/features/events/hooks";
import { useCreateOrder } from "@/features/orders/hooks";
import { hasEventContentFields, hasHomeGroup, getErrorMessage } from "../../lib/graphql.ts";
import { getMinPrice } from "../../lib/format.ts";
import { sanitizeUrl } from "../../lib/url-utils.ts";
import {
  IconAlertTriangle,
  IconCalendar,
  IconMapPin,
  IconTicket,
  IconPlay,
  IconTelegram,
  IconChevronRight,
  IconExternalLink,
  IconCheck,
  IconHomeHeart,
  IconGift,
} from "../../components/Icons.tsx";
import {
  formatDateBangkok,
  formatTimeBangkok,
} from "../../lib/bangkok-time.ts";
import { getTelegram, haptic, safeShowAlert } from "../../lib/telegram.ts";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

interface TicketSelection {
  ticketTypeId: string;
  quantity: number;
}

export function EventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tg = getTelegram();
  const summaryRef = useRef<HTMLDivElement>(null);
  const payButtonRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [videoInView, setVideoInView] = useState(false);

  // Cleanup scroll timer on unmount
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
  }, []);

  const isPreview = searchParams.get("preview") === "true";

  // URQL hooks
  const {
    event,
    isLoading,
    error: eventError,
    refetch: refetchEvent,
  } = useEvent(eventId, { preview: isPreview });
  const { createOrder, isLoading: isCreating } = useCreateOrder();
  const { setRsvp, isLoading: isSettingRsvp } = useSetRsvp();
  const { removeRsvp, isLoading: isRemovingRsvp } = useRemoveRsvp();

  const isRsvping = isSettingRsvp || isRemovingRsvp;
  const error = eventError?.message ?? null;

  const [selections, setSelections] = useState<TicketSelection[]>([]);

  // Lazy-load video: only set src when in viewport
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !event?.videoUrl) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVideoInView(true);
      },
      { rootMargin: "100px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [event?.videoUrl]);

  // Calculate totals (subtotal + fees = total)
  const { subtotal, feesTotal, total } = selections.reduce(
    (acc, sel) => {
      const ticketType = event?.ticketTypes.find(
        (t) => t.id === sel.ticketTypeId
      );
      if (!ticketType) return acc;

      const basePrice = parseFloat(ticketType.price);
      const totalPrice = parseFloat(ticketType.totalPrice ?? ticketType.price);
      const fees = totalPrice - basePrice;

      return {
        subtotal: acc.subtotal + basePrice * sel.quantity,
        feesTotal: acc.feesTotal + fees * sel.quantity,
        total: acc.total + totalPrice * sel.quantity,
      };
    },
    { subtotal: 0, feesTotal: 0, total: 0 }
  );

  // Always use THB as display currency (primary currency for all prices)
  // RUB conversion only happens on payment page when user explicitly selects it
  const currencySymbol = "฿";

  // Create order handler
  const handleCreateOrder = useCallback(async () => {
    if (!eventId || isCreating) return;

    const items = selections
      .filter((s) => s.quantity > 0)
      .map((s) => ({
        ticketTypeId: s.ticketTypeId,
        quantity: s.quantity,
      }));

    if (items.length === 0) return;

    try {
      const order = await createOrder(eventId, items);
      haptic("success");
      navigate(`/order/${order.id}`);
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка создания заказа"));
    }
  }, [eventId, selections, isCreating, navigate, createOrder]);

  const handleRsvp = useCallback(async () => {
    if (!eventId || isRsvping) return;
    try {
      await setRsvp(eventId, "GOING");
      haptic("success");
      refetchEvent();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка записи"));
    }
  }, [eventId, isRsvping, setRsvp, refetchEvent]);

  const handleRemoveRsvp = useCallback(async () => {
    if (!eventId || isRsvping) return;
    try {
      await removeRsvp(eventId);
      haptic("success");
      refetchEvent();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка отмены записи"));
    }
  }, [eventId, isRsvping, removeRsvp, refetchEvent]);

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const updateQuantity = (ticketTypeId: string, delta: number) => {
    haptic("light");
    setSelections((prev) => {
      const existing = prev.find((s) => s.ticketTypeId === ticketTypeId);
      const ticketType = event?.ticketTypes.find((t) => t.id === ticketTypeId);

      if (!ticketType) return prev;

      const maxAvailable = ticketType.available ?? 10;

      if (existing) {
        const newQuantity = Math.max(
          0,
          Math.min(maxAvailable, existing.quantity + delta)
        );
        if (newQuantity === 0) {
          return prev.filter((s) => s.ticketTypeId !== ticketTypeId);
        }
        return prev.map((s) =>
          s.ticketTypeId === ticketTypeId ? { ...s, quantity: newQuantity } : s
        );
      } else if (delta > 0) {
        // Scroll to pay button when first ticket is added
        // Clear any previous scroll timer
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
        scrollTimerRef.current = setTimeout(() => {
          payButtonRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
          scrollTimerRef.current = null;
        }, 100);
        return [...prev, { ticketTypeId, quantity: 1 }];
      }
      return prev;
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error || !event) {
    return (
      <Layout>
        <div className={`${CARD_CLASS} p-4 xs:p-5 sm:p-6 text-center`}>
          <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 xs:mb-4 text-amber-500" />
          <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
            Ошибка
          </h2>
          <p className="text-slate-600 mb-4 xs:mb-6 text-sm">
            {error ?? "Мероприятие не найдено"}
          </p>
          <button onClick={() => navigate("/")} className="btn-primary w-full">
            На главную
          </button>
        </div>
      </Layout>
    );
  }

  // Extract content fields with type safety
  const eventContent = hasEventContentFields(event) ? event : null;
  const program = eventContent?.program;
  const streamYoutubeUrl = eventContent?.streamYoutubeUrl;
  const streamVkUrl = eventContent?.streamVkUrl;
  const telegramChatUrl = eventContent?.telegramChatUrl;
  const mapsUrl = eventContent?.mapsUrl;
  const inviteText = eventContent?.inviteText;
  const slogan = eventContent?.slogan;

  return (
    <Layout>
      {/* Preview mode banner — sticky, всегда виден при предпросмотре */}
      {isPreview && (
        <div className="sticky top-0 z-10 -mx-3 xs:-mx-4 min-[430px]:-mx-5 min-[520px]:-mx-6 sm:-mx-7 px-3 xs:px-4 min-[430px]:px-5 min-[520px]:px-6 sm:px-7 pt-1 pb-3 mb-1 -mt-1 bg-gradient-to-b from-amber-50/95 via-amber-50/98 to-transparent backdrop-blur-sm">
          <div className="rounded-2xl overflow-hidden bg-amber-50/95 shadow-sm border border-amber-200/60 ring-1 ring-amber-200/30 p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="shrink-0 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full">
                Предпросмотр
              </span>
              <span className="text-sm text-amber-900 truncate">
                {event.isActive
                  ? "Режим просмотра из админки"
                  : "Черновик, пользователи не видят"}
              </span>
            </div>
            <button
              onClick={() => navigate(`/admin/events/${eventId}`)}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-amber-900 bg-amber-200/80 hover:bg-amber-300 rounded-xl transition-colors active:scale-[0.98]"
            >
              <span>← К редактированию</span>
            </button>
          </div>
        </div>
      )}

      {/* Ссылка на домашнюю группу (если мероприятие из группы) */}
      {hasHomeGroup(event) && (
        <Link
          to={`/home-group/${event.homeGroup.id}`}
          className="mb-4 flex items-center gap-3 py-3 px-4 rounded-2xl bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 active:bg-slate-50/80 transition-colors no-underline text-inherit"
        >
          <div className="w-10 h-10 rounded-xl bg-rose-50/80 flex items-center justify-center text-rose-600/90 flex-shrink-0">
            <IconHomeHeart className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-800">{event.homeGroup.name}</p>
            <p className="text-sm text-slate-500">К домашней группе</p>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
        </Link>
      )}

      {/* 1. Обложка: видео (если есть) или квадратная картинка */}
      {event.videoUrl ? (
        <div
          ref={videoRef}
          className="rounded-2xl overflow-hidden mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100"
        >
          {videoInView ? (
            <video
              src={event.videoUrl}
              poster={event.imageUrl || undefined}
              className="event-page-video block w-full aspect-square object-cover"
              controls
              playsInline
              preload="none"
            />
          ) : event.imageUrl ? (
            <div
              className="block w-full aspect-square bg-slate-200 bg-cover bg-center"
              style={{ backgroundImage: `url(${event.imageUrl})` }}
            />
          ) : (
            <div className="block w-full aspect-[4/3] max-h-48 max-w-64 mx-auto flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
              ★
            </div>
          )}
        </div>
      ) : (
        <div
          className={`overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100 ${
            event.imageUrl
              ? "aspect-square"
              : "aspect-[4/3] max-h-48 max-w-64 mx-auto"
          }`}
        >
          {event.imageUrl ? (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
              ★
            </div>
          )}
        </div>
      )}

      {/* Единая карточка «О мероприятии» — описание первым, дата/место внизу (уже на баннере) */}
      <div
        className={`${CARD_CLASS} divide-y divide-slate-100 mb-4 lg-mobile:mb-6`}
      >
        {/* Описание и программа — первым, чтобы пользователь сразу читал */}
        {(event.description || program) && (
          <div className="p-4 xs:p-5 md-mobile:p-6">
            {event.description && (
              <div className={event.description && program ? "mb-4" : ""}>
                {program && (
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">
                    Описание
                  </p>
                )}
                <p className="text-sm text-slate-600 whitespace-pre-line">
                  {event.description}
                </p>
              </div>
            )}
            {program && (
              <div>
                {event.description && (
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">
                    Программа
                  </p>
                )}
                <p className="text-sm text-slate-600 whitespace-pre-line">
                  {program}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Ссылки: трансляция + чат — компактно */}
        {(streamYoutubeUrl || streamVkUrl || telegramChatUrl) && (
          <div className="p-4 xs:p-5 md-mobile:p-6">
            <div className="flex flex-wrap gap-2">
              {streamYoutubeUrl && sanitizeUrl(streamYoutubeUrl) && (
                <a
                  href={sanitizeUrl(streamYoutubeUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 active:scale-[0.98] transition-all no-underline text-inherit [-webkit-tap-highlight-color:transparent]"
                >
                  <div className="w-9 h-9 rounded-full bg-red-50/80 text-red-700/75 flex items-center justify-center flex-shrink-0">
                    <IconPlay className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">
                    YouTube
                  </span>
                </a>
              )}
              {streamVkUrl && sanitizeUrl(streamVkUrl) && (
                <a
                  href={sanitizeUrl(streamVkUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 active:scale-[0.98] transition-all no-underline text-inherit [-webkit-tap-highlight-color:transparent]"
                >
                  <div className="w-9 h-9 rounded-full bg-blue-50/80 text-blue-700/75 flex items-center justify-center flex-shrink-0">
                    <IconPlay className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">
                    ВКонтакте
                  </span>
                </a>
              )}
              {telegramChatUrl && sanitizeUrl(telegramChatUrl) && (
                <a
                  href={sanitizeUrl(telegramChatUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 active:scale-[0.98] transition-all no-underline text-inherit [-webkit-tap-highlight-color:transparent]"
                >
                  <div className="w-9 h-9 rounded-full bg-sky-50/80 text-sky-700/75 flex items-center justify-center flex-shrink-0">
                    <IconTelegram className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-800">
                    Чат
                  </span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Дата и место — сразу после трансляций */}
        <div className="p-4 xs:p-5 md-mobile:p-6 space-y-3">
          <p className="text-sm text-slate-600 flex items-center gap-2">
            <IconCalendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {formatDateBangkok(event.startsAt, {
              weekday: "long",
              day: "numeric",
              month: "long",
              hour: "2-digit",
              minute: "2-digit",
            })}
            {event.endsAt && ` — до ${formatTimeBangkok(event.endsAt)}`}
          </p>
          {(event.venue || event.address || mapsUrl) &&
            (mapsUrl && sanitizeUrl(mapsUrl) ? (
              <a
                href={sanitizeUrl(mapsUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 py-2.5 px-3 -mx-1 rounded-xl bg-teal-50/80 border border-teal-200/60 no-underline text-inherit active:scale-[0.98] transition-all [-webkit-tap-highlight-color:transparent]"
              >
                <div className="w-9 h-9 rounded-full bg-teal-50/80 text-teal-700/75 flex items-center justify-center flex-shrink-0">
                  <IconMapPin className="w-4 h-4" />
                </div>
                <span className="min-w-0 flex-1 text-sm text-slate-700">
                  {event.venue && (
                    <span className="font-medium text-slate-800">
                      {event.venue}
                    </span>
                  )}
                  {event.venue && event.address && " · "}
                  {event.address && <span>{event.address}</span>}
                  {!event.venue && !event.address && (
                    <span className="font-medium text-slate-800">Карта</span>
                  )}
                </span>
                <div className="w-8 h-8 rounded-full bg-teal-200/80 text-teal-700 flex items-center justify-center flex-shrink-0">
                  <IconExternalLink className="w-4 h-4" />
                </div>
              </a>
            ) : (
              <p className="text-sm text-slate-600 flex items-start gap-2">
                <IconMapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <span className="min-w-0">
                  {event.venue && <span className="block">{event.venue}</span>}
                  {event.address && (
                    <span className="block text-slate-500">
                      {event.address}
                    </span>
                  )}
                </span>
              </p>
            ))}
        </div>

        {/* Приглашение / слоган — внизу, ближе к билетам */}
        {(inviteText || slogan) && (
          <div className="p-4 xs:p-5 md-mobile:p-6 text-center">
            {inviteText && (
              <p className="font-medium text-slate-800">{inviteText}</p>
            )}
            {slogan && (
              <p className="text-sm text-slate-600 mt-1">{slogan}</p>
            )}
          </div>
        )}
      </div>

      {/* Мои билеты — если уже куплены */}
      {event.hasMyTickets && (
        <div className="mb-4 lg-mobile:mb-6">
          <div className="rounded-2xl overflow-hidden bg-teal-50/95 shadow-sm border border-teal-200/60 ring-1 ring-teal-200/30 p-4 md-mobile:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100/80 text-teal-700/75 flex items-center justify-center flex-shrink-0">
                <IconTicket className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">У вас есть билеты</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Перейти к заказам и билетам
                </p>
              </div>
              <button
                onClick={() => navigate(`/orders?eventId=${event.id}`)}
                className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-teal-800 bg-teal-200/80 hover:bg-teal-300/80 rounded-xl transition-colors active:scale-[0.98]"
              >
                <span>Мои заказы</span>
                <IconChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Розыгрыш - показываем во всех статусах кроме DRAFT */}
      {event.activeRaffle && (
        <div className="mb-4 lg-mobile:mb-6">
          <Link
            to={`/raffle/${event.activeRaffle.id}`}
            className={`rounded-2xl overflow-hidden shadow-lg p-4 md-mobile:p-5 flex items-center gap-3 no-underline text-white active:scale-[0.98] transition-transform ${
              event.activeRaffle.status === "COMPLETED"
                ? "bg-gradient-to-r from-emerald-500 to-teal-600"
                : event.activeRaffle.status === "IN_PROGRESS"
                ? "bg-gradient-to-r from-purple-500 to-indigo-600"
                : "bg-gradient-to-r from-slate-500 to-slate-600"
            }`}
          >
            <div className={`w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 ${
              event.activeRaffle.status === "IN_PROGRESS" ? "animate-pulse" : ""
            }`}>
              <IconGift className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-lg">
                {event.activeRaffle.status === "COMPLETED"
                  ? "🏆 Результаты розыгрыша"
                  : event.activeRaffle.status === "IN_PROGRESS" 
                  ? "🎲 Идёт розыгрыш!" 
                  : "⏳ Ожидайте розыгрыш"}
              </p>
              <p className="text-sm text-white/80 mt-0.5">
                {event.activeRaffle.name}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-xl transition-colors">
              <span>
                {event.activeRaffle.status === "COMPLETED"
                  ? "Смотреть"
                  : event.activeRaffle.status === "IN_PROGRESS"
                  ? "Смотреть"
                  : "Подробнее"}
              </span>
              <IconChevronRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      )}

      {/* Гендер пати - показываем во всех статусах кроме DRAFT */}
      {event.activeGenderParty && (
        <div className="mb-4 lg-mobile:mb-6">
          <Link
            to={`/gender-party/${event.activeGenderParty.id}`}
            className={`rounded-2xl overflow-hidden shadow-lg p-4 md-mobile:p-5 flex items-center gap-3 no-underline text-white active:scale-[0.98] transition-transform ${
              event.activeGenderParty.status === "COMPLETED"
                ? "bg-gradient-to-r from-sky-500 to-pink-500"
                : event.activeGenderParty.status === "VOTING_OPEN"
                ? "bg-gradient-to-r from-sky-400 to-pink-400"
                : "bg-gradient-to-r from-slate-500 to-slate-600"
            }`}
          >
            <div className={`w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-2xl ${
              event.activeGenderParty.status === "VOTING_OPEN" ? "animate-pulse" : ""
            }`}>
              👶
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-lg">
                {event.activeGenderParty.status === "COMPLETED"
                  ? "🎉 Результаты голосования"
                  : event.activeGenderParty.status === "VOTING_OPEN"
                  ? "💙🩷 Идёт голосование!"
                  : "⏳ Ожидайте голосование"}
              </p>
              <p className="text-sm text-white/80 mt-0.5">
                {event.activeGenderParty.name}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-xl transition-colors">
              <span>
                {event.activeGenderParty.status === "VOTING_OPEN"
                  ? "Голосовать"
                  : "Смотреть"}
              </span>
              <IconChevronRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      )}

      {/* Вы записаны — бесплатное мероприятие, RSVP без билетов */}
      {!event.hasMyTickets && event.myRsvp?.status === "GOING" && (
        <div className="mb-4 lg-mobile:mb-6">
          <div className="rounded-2xl overflow-hidden bg-teal-50/95 shadow-sm border border-teal-200/60 ring-1 ring-teal-200/30 p-4 md-mobile:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100/80 text-teal-700/75 flex items-center justify-center flex-shrink-0">
                <IconCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">Вы записаны</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Ждём вас на мероприятии
                </p>
              </div>
              <button
                onClick={handleRemoveRsvp}
                disabled={isRsvping}
                className="shrink-0 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-teal-100/80 rounded-xl transition-colors disabled:opacity-50"
              >
                Отменить запись
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ожидайте подтверждения — для мероприятий с requiresRsvpConfirmation */}
      {!event.hasMyTickets && event.myRsvp?.status === "PENDING" && (
        <div className="mb-4 lg-mobile:mb-6">
          <div className="rounded-2xl overflow-hidden bg-amber-50/95 shadow-sm border border-amber-200/60 ring-1 ring-amber-200/30 p-4 md-mobile:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100/80 text-amber-700/75 flex items-center justify-center flex-shrink-0">
                <IconAlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">
                  Ожидайте подтверждения
                </p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Организатор подтвердит вашу запись. Адрес и карта будут
                  доступны после подтверждения.
                </p>
              </div>
              <button
                onClick={handleRemoveRsvp}
                disabled={isRsvping}
                className="shrink-0 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-amber-100/80 rounded-xl transition-colors disabled:opacity-50"
              >
                Отменить запись
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Записаться — бесплатное мероприятие без регистрации (только для будущих) */}
      {!event.hasMyTickets &&
        event.myRsvp?.status !== "GOING" &&
        event.myRsvp?.status !== "PENDING" &&
        new Date(event.startsAt) > new Date() &&
        (event.ticketTypes.length === 0 || getMinPrice(event).price === 0) && (
          <div className="mb-4 lg-mobile:mb-6">
            <button
              onClick={handleRsvp}
              disabled={isRsvping}
              className="btn-primary w-full"
            >
              {isRsvping ? "Записываюсь..." : "Записаться"}
            </button>
          </div>
        )}

      {/* Билеты + Итого */}
      <div
        ref={summaryRef}
        className={`${CARD_CLASS} p-3 xs:p-4 md-mobile:p-5`}
      >
        {event.ticketTypes.length === 0 ? (
          <div className="py-6 text-center">
            <IconTicket className="w-10 h-10 text-emerald-400/70 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">Бесплатно</p>
          </div>
        ) : event.ticketTypes.every(
            (t) => t.available !== null && t.available === 0
          ) ? (
          <div className="py-6 text-center">
            <IconTicket className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Нет доступных билетов</p>
          </div>
        ) : (
          <div className="space-y-2">
            {event.ticketTypes.map((ticketType) => {
              const selection = selections.find(
                (s) => s.ticketTypeId === ticketType.id
              );
              const quantity = selection?.quantity ?? 0;
              const available = ticketType.available;
              // Always display THB symbol - prices from API are now THB by default
              // RUB conversion happens on payment page only
              const ticketCurrency = "฿";

              return (
                <div
                  key={ticketType.id}
                  className={`rounded-xl p-3 border transition-all flex items-center justify-between gap-3 ${
                    quantity > 0
                      ? "border-2 border-[var(--color-accent)] bg-[var(--color-accent)]/5"
                      : "rounded-xl bg-white/95 shadow-sm border border-slate-100"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="font-medium text-slate-900 text-[15px]">
                        {ticketType.name}
                      </h3>
                      <span className="font-semibold text-[var(--color-accent)] text-[15px]">
                        {ticketType.price} {ticketCurrency}
                      </span>
                    </div>
                    {ticketType.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {ticketType.description}
                      </p>
                    )}
                    {available != null && available <= 10 && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Осталось: {available}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => updateQuantity(ticketType.id, -1)}
                      disabled={quantity === 0}
                      className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors text-base"
                      aria-label="Уменьшить"
                    >
                      −
                    </button>
                    <span className="w-7 text-center text-sm font-semibold text-slate-900 tabular-nums">
                      {quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(ticketType.id, 1)}
                      disabled={available != null && quantity >= available}
                      className="w-9 h-9 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center hover:bg-[var(--color-accent-hover)] disabled:opacity-40 transition-colors text-base"
                      aria-label="Увеличить"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > 0 && (
          <div className="border-t border-slate-200 mt-3 xs:mt-4 pt-3 xs:pt-4">
            <div className="space-y-1">
              <div className="flex justify-between text-xs xs:text-sm text-slate-600">
                <span>Билеты</span>
                <span>
                  {subtotal} {currencySymbol}
                </span>
              </div>
              {feesTotal > 0 && (
                <div className="flex justify-between text-xs xs:text-sm text-slate-500">
                  <span>Сервисный сбор</span>
                  <span>
                    {feesTotal} {currencySymbol}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <span className="font-medium text-slate-900 text-sm xs:text-base">
                  К оплате
                </span>
                <span className="font-bold text-base xs:text-lg text-[var(--color-accent)]">
                  {total} {currencySymbol}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {total > 0 && (
        <button
          ref={payButtonRef}
          onClick={handleCreateOrder}
          disabled={isCreating}
          className="btn-primary w-full mt-4 xs:mt-6 scroll-mb-24"
        >
          {isCreating
            ? "Создание заказа..."
            : `Оплатить ${total} ${currencySymbol}`}
        </button>
      )}
    </Layout>
  );
}
